/**
 * LibraryReport.jsx
 * Full-featured Library Book Search & Transaction Report
 * Stack: React + Tailwind CSS (CDN) + Django REST API
 *
 * API base: adjust BASE_URL to match your Django server
 */

import { useState, useCallback } from "react";
import AppURL from "../../../../widgets/AppURL";
import { fetchDataFromApi } from "../../../../widgets/api";


// ── helpers ──────────────────────────────────────────────
const cls = (...c) => c.filter(Boolean).join(" ");


// ── small reusable components ────────────────────────────
const Badge = ({ label, color }) => {
  const map = {
    green: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    red:   "bg-rose-100   text-rose-800   ring-rose-200",
    blue:  "bg-blue-100   text-blue-800   ring-blue-200",
    gray:  "bg-slate-100  text-slate-600  ring-slate-200",
  };
  return (
    <span className={cls("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1", map[color] || map.gray)}>
      {label}
    </span>
  );
};

const Spinner = () => (
  <div className="flex justify-center py-16">
    <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
  </div>
);

const EmptyState = ({ icon, msg }) => (
  <div className="flex flex-col items-center gap-3 py-20 text-slate-400">
    <span className="text-5xl">{icon}</span>
    <p className="text-sm font-medium">{msg}</p>
  </div>
);

// ── TAB 1 : Book Search Report ───────────────────────────
function BookSearchTab() {
  const [query, setQuery]   = useState("");
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");
  const [expanded, setExpanded] = useState({});

  const search = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true); setError(""); setData(null);
    try {
      const res = await fetchDataFromApi(`${AppURL.LibraryURL}library-book-search-report-list-view/?name=${encodeURIComponent(query)}`);
      setData(res);
    } catch (e) {
      setError("Failed to fetch. Is Django running?");
    } finally {
      setLoading(false);
    }
  }, [query]);

  const toggle = (i) => setExpanded(p => ({ ...p, [i]: !p[i] }));

  return (
    <div className="space-y-6">
      {/* Search bar */}
      <div className="flex gap-3">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()}
          placeholder="Search by book name… e.g. Financial Management"
          className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
        />
        <button
          onClick={search}
          className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow transition active:scale-95"
        >
          Search
        </button>
      </div>

      {error && <p className="text-rose-500 text-sm">{error}</p>}
      {loading && <Spinner />}

      {data && !loading && (
        <>
          <p className="text-xs text-slate-500">
            Found <strong>{data.total}</strong> title{data.total !== 1 ? "s" : ""}
          </p>

          {data.results.length === 0 ? (
            <EmptyState icon="📚" msg="No books found." />
          ) : (
            <div className="space-y-4">
              {data.results.map((book, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  {/* Title row */}
                  <div className="flex items-start justify-between gap-4 px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-slate-800 truncate">{book.book_name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{book.book_name_nep}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-600">
                        <span>✍️ {book.author}</span>
                        <span>🏛️ {book.publisher}</span>
                        <span>📅 Ed. {book.edition}</span>
                      </div>
                    </div>
                    {/* Counters */}
                    <div className="flex gap-3 shrink-0 text-center">
                      {[
                        { label: "Total",     val: book.total_copies, color: "bg-slate-100 text-slate-700" },
                        { label: "Available", val: book.available,    color: "bg-emerald-100 text-emerald-700" },
                        { label: "Issued",    val: book.issued,       color: "bg-rose-100 text-rose-700" },
                      ].map(({ label, val, color }) => (
                        <div key={label} className={cls("rounded-xl px-3 py-2 min-w-[56px]", color)}>
                          <div className="text-xl font-extrabold leading-none">{val}</div>
                          <div className="text-[10px] font-medium mt-0.5">{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Copies accordion */}
                  <div className="border-t border-slate-100">
                    <button
                      onClick={() => toggle(i)}
                      className="w-full flex items-center justify-between px-5 py-2.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition"
                    >
                      {expanded[i] ? "▲ Hide copies" : `▼ Show ${book.total_copies} copies`}
                    </button>

                    {expanded[i] && (
                      <div className="px-4 pb-4 overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-slate-500 border-b border-slate-100">
                              {["Book ID","Call No","DDC No","ISBN","Price","Status"].map(h => (
                                <th key={h} className="py-2 pr-4 font-semibold whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {book.copies.map((c) => (
                              <tr key={c.book_id} className="border-b border-slate-50 hover:bg-slate-50 transition">
                                <td className="py-1.5 pr-4 font-mono text-slate-700">{c.book_id}</td>
                                <td className="py-1.5 pr-4 text-slate-600">{c.call_no || "—"}</td>
                                <td className="py-1.5 pr-4 text-slate-600">{c.ddc_no || "—"}</td>
                                <td className="py-1.5 pr-4 text-slate-600">{c.isbn_no || "—"}</td>
                                <td className="py-1.5 pr-4 text-slate-600">{c.actual_price ? `Rs. ${c.actual_price}` : "—"}</td>
                                <td className="py-1.5 pr-4">
                                  <Badge
                                    label={c.status}
                                    color={c.status === "Available" ? "green" : "red"}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── TAB 2 : Transaction Report ───────────────────────────
function TransactionTab() {
  const [filters, setFilters] = useState({
    book_name: "", member_id: "", returned: "", from_date: "", to_date: "",
  });
  const [data, setData]     = useState(null);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const buildQS = (p = 1) => {
    const params = new URLSearchParams({ page: p, page_size: 15 });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    return params.toString();
  };

  const load = useCallback(async (p = 1) => {
    setLoading(true); setError("");
    try {
      const res = await fetchDataFromApi(`/transactions/report/?${buildQS(p)}`);
      setData(res); setPage(p);
    } catch {
      setError("Failed to fetch transactions.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const upd = (k) => (e) => setFilters(p => ({ ...p, [k]: e.target.value }));

  const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 transition";

  return (
    <div className="space-y-6">
      {/* Filter panel */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Filters</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Book Name</label>
            <input value={filters.book_name} onChange={upd("book_name")} placeholder="e.g. Financial" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Member ID</label>
            <input value={filters.member_id} onChange={upd("member_id")} placeholder="e.g. 150" className={inputCls} type="number" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Status</label>
            <select value={filters.returned} onChange={upd("returned")} className={inputCls}>
              <option value="">All</option>
              <option value="true">Returned</option>
              <option value="false">Not Returned</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">From Date</label>
            <input value={filters.from_date} onChange={upd("from_date")} type="date" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To Date</label>
            <input value={filters.to_date} onChange={upd("to_date")} type="date" className={inputCls} />
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={() => load(1)} className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow transition active:scale-95">
            Apply Filters
          </button>
          <button onClick={() => { setFilters({ book_name:"", member_id:"", returned:"", from_date:"", to_date:"" }); setData(null); }}
            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition">
            Clear
          </button>
        </div>
      </div>

      {error && <p className="text-rose-500 text-sm">{error}</p>}
      {loading && <Spinner />}

      {data && !loading && (
        <>
          {/* Summary strip */}
          <div className="flex flex-wrap gap-4 text-sm">
            {[
              { label: "Total Records", val: data.total, color: "text-slate-700" },
              { label: "Page",          val: `${data.page} / ${data.total_pages}`, color: "text-indigo-600" },
            ].map(({ label, val, color }) => (
              <div key={label} className="flex items-center gap-1.5">
                <span className="text-slate-400">{label}:</span>
                <span className={cls("font-bold", color)}>{val}</span>
              </div>
            ))}
          </div>

          {data.results.length === 0 ? (
            <EmptyState icon="🔍" msg="No transactions match." />
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["SN","Book","Author","Member","Issued (EN)","Issued (NP)","Return (EN)","Return (NP)","Status","Remarks"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.results.map((t) => (
                    <tr key={t.sn} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-2.5 font-mono text-slate-400">{t.sn}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-slate-800 whitespace-nowrap">{t.book_name}</div>
                        <div className="text-[10px] text-slate-400">ID: {t.book_id}</div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{t.author}</td>
                      <td className="px-4 py-2.5 font-mono text-slate-600">{t.member_id}</td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{t.issued_date_e}</td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{t.issued_date_n}</td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{t.return_date_e || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{t.returned_date_n || "—"}</td>
                      <td className="px-4 py-2.5">
                        <Badge label={t.returned ? "Returned" : "Issued"} color={t.returned ? "green" : "red"} />
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{t.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => load(page - 1)} disabled={page === 1}
                className="px-4 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
              >← Prev</button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(data.total_pages, 7) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <button key={p} onClick={() => load(p)}
                      className={cls("w-8 h-8 rounded-lg text-sm font-semibold transition",
                        p === page ? "bg-indigo-600 text-white shadow" : "text-slate-600 hover:bg-slate-100"
                      )}>{p}</button>
                  );
                })}
              </div>
              <button
                onClick={() => load(page + 1)} disabled={page === data.total_pages}
                className="px-4 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
              >Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Root App ─────────────────────────────────────────────
const TABS = [
  { id: "search",       label: "📚 Book Search & Availability" },
];

export default function LibrarySearchReport() {
  const [tab, setTab] = useState("search");

  return (
    <div className="min-h-screen bg-gradient-to-br font-public-sans from-slate-50 via-white to-indigo-50">
           {/* Tabs */}
      <div className="max-w-6xl mx-auto">
        <div className="flex gap-2 bg-slate-100 rounded-xl p-1 w-fit mb-8">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cls(
                "px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                tab === t.id
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Panel */}
        <div className="pb-16">
          {tab === "search"      && <BookSearchTab />}
        </div>
      </div>
    </div>
  );
}